import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { DEFAULT_CATEGORIES, type Category } from "@instead/shared";
import { api } from "../lib/api.ts";
import { useT } from "../lib/i18n.ts";
import { goBackTo } from "../lib/nav.ts";
import { catLabel, useReorder } from "../components/DealCard.tsx";
import { CatIcon, Ic } from "../components/Icons.tsx";
import { Dialog, Info, InlineName, Toast, useToast } from "../components/Ui.tsx";

/**
 * The one place categories are edited (round 4). Round 5: everything in
 * place — the pencil turns the name into a field, "+" adds a row you type
 * into, drag the handle to reorder, and Reset to defaults appears once
 * something differs from the six built-ins.
 */
export function Categories() {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as { back?: string };
  const { data } = useQuery({ queryKey: ["categories"], queryFn: api.categories.list });
  const cats = data?.categories ?? [];
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState<{ parentId: string | null } | null>(null);
  const [confirm, setConfirm] = useState<Category | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [toast, say] = useToast();
  const refresh = () => qc.invalidateQueries({ queryKey: ["categories"] });

  // top-level order while a drag is in flight; the server's order otherwise
  const [order, setOrder] = useState<string[] | null>(null);
  const top = cats.filter((c) => !c.parentId);
  const topIds = order ?? top.map((c) => c.id);
  const shownTop = topIds.map((id) => top.find((c) => c.id === id)).filter((c): c is Category => !!c);
  const { move: moveTo, handleProps, cardProps } = useReorder(topIds, setOrder, (next) => void persistOrder(next));

  /** Hand the existing sort slots out in the new order; only changed rows are written. */
  async function persistOrder(next: string[]) {
    const slots = top.map((c) => c.sortOrder).sort((a, b) => a - b);
    const writes = next.map((id, i) => ({ id, sortOrder: slots[i]! })).filter((w) => top.find((c) => c.id === w.id)?.sortOrder !== w.sortOrder);
    for (const w of writes) await api.categories.update(w.id, { sortOrder: w.sortOrder });
    if (writes.length) await refresh();
    setOrder(null);
  }

  async function rename(c: Category, name: string) {
    setEditing(null);
    if (!name.trim() || name.trim() === c.name) return;
    await api.categories.update(c.id, { name: name.trim() });
    await refresh();
    say(t("saved"));
  }

  async function create(name: string) {
    const parentId = adding?.parentId ?? null;
    setAdding(null);
    if (!name.trim()) return;
    await api.categories.create({ name: name.trim(), ...(parentId ? { parentId } : {}) });
    await refresh();
    say(t("saved"));
  }

  // anything off the seed: a built-in renamed, or the built-ins out of their seed order
  const builtIns = top.filter((c) => c.kind !== "custom");
  const changed =
    builtIns.some((c) => DEFAULT_CATEGORIES.find((d) => d.kind === c.kind)?.name !== c.name) ||
    builtIns.map((c) => c.kind).join(",") !== DEFAULT_CATEGORIES.map((d) => d.kind).join(",");

  async function reset() {
    setConfirmReset(false);
    await api.categories.reset();
    await refresh();
    say(t("saved"));
  }

  const goBack = () => void goBackTo(nav, search.back, "/settings/shelf");
  const newRow = (parentId: string | null) => (
    <div className="nav-row" style={{ cursor: "default", paddingLeft: parentId ? 28 : 0 }}>
      <InlineName label={t("cats.name")} value="" placeholder={parentId ? t("cats.newSub") : t("cats.new")} onDone={create} />
    </div>
  );

  return (
    <div>
      <div className="screen-head">
        <button className="icon-btn" aria-label={t("back")} onClick={goBack}>{Ic.back()}</button>
        <h1 className="title">{t("cats.title")}</h1>
        <button className="btn btn-primary" style={{ minHeight: 36 }} onClick={() => setAdding({ parentId: null })}>{Ic.plus({ size: 14 })} {t("cats.new")}</button>
      </div>
      <div>
        {shownTop.map((c) => {
          const label = catLabel(c, t);
          return (
            <div key={c.id}>
              <div className="nav-row" style={{ cursor: "default", flexWrap: "wrap" }} {...cardProps(c.id)}>
                <button type="button" className="icon-btn drag-handle" aria-label={t("deal.reorder")} title={t("deal.reorderHint")} {...handleProps(c.id, label)}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp") { e.preventDefault(); moveTo(c.id, -1); }
                    if (e.key === "ArrowDown") { e.preventDefault(); moveTo(c.id, 1); }
                  }}>{Ic.drag({ size: 16 })}</button>
                <span style={{ color: `var(--${c.colorToken})`, display: "inline-flex" }}><CatIcon kind={c.kind} size={16} /></span>
                {editing === c.id ? (
                  <InlineName label={t("cats.name")} value={label} onDone={(v) => rename(c, v)} />
                ) : (
                  <>
                    <span style={{ minWidth: 0 }}>{label}<span className="meta" style={{ marginLeft: 6 }}>{c.itemCount ?? 0}</span></span>
                    <button className="icon-btn" style={{ minWidth: 32, minHeight: 32, padding: 0 }} aria-label={`${t("edit")} ${label}`} onClick={() => setEditing(c.id)}>{Ic.edit({ size: 14 })}</button>
                    {c.kind === "video" && <Info text={t("cats.videoInfo")} label={`${t("settings.about")} ${label}`} />}
                    <span className="grow" />
                    <button className="icon-btn" aria-label={`${t("cats.newSub")} ${label}`} onClick={() => setAdding({ parentId: c.id })}>{Ic.plus({ size: 14 })}</button>
                    {c.kind === "custom" && <button className="icon-btn danger" aria-label={`${t("delete")} ${c.name}`} onClick={() => setConfirm(c)}>{Ic.trash({ size: 14 })}</button>}
                  </>
                )}
              </div>
              {cats.filter((k) => k.parentId === c.id).map((k) => (
                <div key={k.id} className="nav-row" style={{ paddingLeft: 28, cursor: "default" }}>
                  {editing === k.id ? (
                    <InlineName label={t("cats.name")} value={k.name} onDone={(v) => rename(k, v)} />
                  ) : (
                    <>
                      <span style={{ minWidth: 0 }}>{k.name}<span className="meta" style={{ marginLeft: 6 }}>{k.itemCount ?? 0}</span></span>
                      <button className="icon-btn" style={{ minWidth: 32, minHeight: 32, padding: 0 }} aria-label={`${t("edit")} ${k.name}`} onClick={() => setEditing(k.id)}>{Ic.edit({ size: 14 })}</button>
                      <span className="grow" />
                      <button className="icon-btn danger" aria-label={`${t("delete")} ${k.name}`} onClick={() => setConfirm(k)}>{Ic.trash({ size: 14 })}</button>
                    </>
                  )}
                </div>
              ))}
              {adding?.parentId === c.id && newRow(c.id)}
            </div>
          );
        })}
        {adding && adding.parentId === null && newRow(null)}
      </div>

      {changed && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "var(--sp-5)" }}>
          <button className="icon-btn" onClick={() => setConfirmReset(true)}>{Ic.refresh({ size: 14 })} {t("cats.reset")}</button>
        </div>
      )}

      {confirm && (
        <Dialog title={t("cats.deleteQ", { name: confirm.name })} onClose={() => setConfirm(null)}>
          <div className="dialog-actions">
            <button className="btn" onClick={() => setConfirm(null)}>{t("keep")}</button>
            <button className="btn btn-danger" onClick={async () => { await api.categories.remove(confirm.id); setConfirm(null); await refresh(); await qc.invalidateQueries({ queryKey: ["items"] }); }}>{t("delete")}</button>
          </div>
        </Dialog>
      )}
      {confirmReset && (
        <Dialog title={t("cats.resetQ")} onClose={() => setConfirmReset(false)}>
          <div className="dialog-actions">
            <button className="btn" onClick={() => setConfirmReset(false)}>{t("cancel")}</button>
            <button className="btn btn-primary" onClick={reset}>{t("cats.reset")}</button>
          </div>
        </Dialog>
      )}
      <Toast msg={toast} />
    </div>
  );
}

